import { supabase } from '../lib/supabase';

export const analyticsService = {
  // Get agent analytics data
  async getAgentAnalytics() {
    try {
      // Get all agents
      const { data: agents, error: agentsError } = await supabase
        ?.from('agents')
        ?.select('*')
        ?.order('name');

      if (agentsError) throw agentsError;

      // Get all conversations
      const { data: conversations, error: conversationsError } = await supabase
        ?.from('chat_conversations')
        ?.select('*');

      if (conversationsError) throw conversationsError;

      // Get all messages
      const { data: messages, error: messagesError } = await supabase
        ?.from('chat_messages')
        ?.select('*')
        ?.order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      // Calculate metrics for each agent
      const agentMetrics = agents?.map(agent => {
        const agentConversations = conversations?.filter(c => c?.agent_id === agent?.id) || [];
        const agentMessages = messages?.filter(m => m?.sender_type === 'agent' && m?.sender_id === agent?.id) || [];

        // Calculate average response time (time between user message and agent response)
        let totalResponseTime = 0;
        let responseCount = 0;

        agentConversations?.forEach(conv => {
          const convMessages = messages?.filter(m => m?.conversation_id === conv?.id) || [];
          for (let i = 0; i < convMessages?.length - 1; i++) {
            if (convMessages?.[i]?.sender_type === 'user' && convMessages?.[i + 1]?.sender_type === 'agent') {
              const userTime = new Date(convMessages[i]?.created_at)?.getTime();
              const agentTime = new Date(convMessages[i + 1]?.created_at)?.getTime();
              totalResponseTime += (agentTime - userTime) / 1000; // Convert to seconds
              responseCount++;
            }
          }
        });

        const avgResponseTime = responseCount > 0 ? totalResponseTime / responseCount : 0;

        // Calculate completion rate (conversations with at least one agent response)
        const completedConversations = agentConversations?.filter(conv => {
          return messages?.some(m => m?.conversation_id === conv?.id && m?.sender_type === 'agent');
        })?.length || 0;
        const completionRate = agentConversations?.length > 0 
          ? (completedConversations / agentConversations?.length) * 100 
          : 0;

        // Simulate accuracy score (in real app, this would come from quality reviews)
        const accuracyScore = 85 + Math.random() * 15; // 85-100%

        // Simulate project success rate
        const successRate = 75 + Math.random() * 25; // 75-100%

        return {
          id: agent?.id,
          name: agent?.name,
          role: agent?.role,
          status: agent?.status,
          avatarUrl: agent?.avatar_url,
          specialization: agent?.specialization,
          avgResponseTime: Math.round(avgResponseTime),
          completionRate: Math.round(completionRate * 10) / 10,
          accuracyScore: Math.round(accuracyScore * 10) / 10,
          successRate: Math.round(successRate * 10) / 10,
          totalAnalyses: agentMessages?.length || 0,
          activeConversations: agentConversations?.length || 0
        };
      }) || [];

      return agentMetrics;
    } catch (error) {
      console.error('Error fetching agent analytics:', error);
      throw error;
    }
  },

  // Get performance trends over time
  async getPerformanceTrends() {
    try {
      const { data: messages, error } = await supabase
        ?.from('chat_messages')
        ?.select('created_at, sender_type')
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by date and calculate daily metrics
      const dailyMetrics = {};
      messages?.forEach(msg => {
        const date = new Date(msg?.created_at)?.toISOString()?.split('T')?.[0];
        if (!dailyMetrics?.[date]) {
          dailyMetrics[date] = { date, userMessages: 0, agentMessages: 0 };
        }
        if (msg?.sender_type === 'user') {
          dailyMetrics[date].userMessages++;
        } else {
          dailyMetrics[date].agentMessages++;
        }
      });

      return Object.values(dailyMetrics)?.slice(-30); // Last 30 days
    } catch (error) {
      console.error('Error fetching performance trends:', error);
      throw error;
    }
  },

  // Get team-wide KPIs
  async getTeamKPIs() {
    try {
      const { data: agents } = await supabase?.from('agents')?.select('*');
      const { data: conversations } = await supabase?.from('chat_conversations')?.select('*');
      const { data: messages } = await supabase?.from('chat_messages')?.select('*');

      const activeAgents = agents?.filter(a => a?.status === 'available')?.length || 0;
      const totalConversations = conversations?.length || 0;
      const totalMessages = messages?.length || 0;
      const avgMessagesPerConversation = totalConversations > 0 
        ? Math.round((totalMessages / totalConversations) * 10) / 10 
        : 0;

      return {
        activeAgents,
        totalConversations,
        totalMessages,
        avgMessagesPerConversation
      };
    } catch (error) {
      console.error('Error fetching team KPIs:', error);
      throw error;
    }
  }
};